import {
    Button,
    ButtonGroup,
    Checkbox,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@material-ui/core';
import AdminHeader from '../../../../components/atom/AdminHeader';
import { formatMoney, formatDateTime } from '../../../../utils';
import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';

function BatchPayoutTable({ batch, payouts, payoutsToBatch, setPayoutsToBatch, onSave }) {
    const allSelectedAmount = payouts.reduce((acc, payout) => {
        const inBatch = payoutsToBatch.find(p => p.payout_id === payout.id);
        return acc + (inBatch ? payout.amountDue : 0);
    }, 0);

    const allPayoutsAmount = payouts.reduce((acc, payout) => {
        const inBatch = payoutsToBatch.find(p => p.payout_id === payout.id);
        return acc + (inBatch ? inBatch.pay_amount : 0);
    }, 0);

    const isCheckedAll = payouts.length > 0 && payoutsToBatch.length === payouts.length;
    const [allPaymentsChecked, setAllPaymentsChecked] = useState(isCheckedAll);

    const initialPayedRows = useMemo(
        () => payoutsToBatch,
        // keep the initial value only on first render
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleResetInitalPayouts = () => {
        setPayoutsToBatch(() => initialPayedRows);
    };

    useEffect(() => {
        setAllPaymentsChecked(isCheckedAll);
    }, [isCheckedAll]);

    const handleCheckAllPayments = () => {
        setAllPaymentsChecked(!allPaymentsChecked);
        if (!allPaymentsChecked) {
            // we should keep the existing notes and pay_amount if any
            if (payoutsToBatch.length > 0) {
                const newPayoutsToBatch = payouts.map(payout => {
                    const existing = payoutsToBatch.find(p => p.payout_id === payout.id);
                    if (existing) {
                        return existing;
                    } else {
                        return {
                            payout_id: payout.id,
                            pay_amount: payout.amountDue,
                            note: ''
                        };
                    }
                });
                setPayoutsToBatch(newPayoutsToBatch);
            } else {
                setPayoutsToBatch(
                    payouts.map(payout => ({
                        payout_id: payout.id,
                        pay_amount: payout.amountDue,
                        note: ''
                    }))
                );
            }
        } else {
            setPayoutsToBatch([]);
        }
    };

    return (
        <Grid container>
            <Grid
                item
                xs={12}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <ButtonGroup variant="contained">
                    <Button
                        startIcon={
                            <Checkbox
                                size="small"
                                checked={allPaymentsChecked}
                                style={{
                                    padding: '2.5px'
                                }}
                            />
                        }
                        onClick={handleCheckAllPayments}
                        color="inherit"
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            justifyContent: 'flex-start'
                        }}
                    >
                        Pay all
                    </Button>
                    <Button
                        onClick={handleResetInitalPayouts}
                        color="primary"
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            padding: '2.5px 9px'
                        }}
                    >
                        Restore last saved
                    </Button>
                </ButtonGroup>
                <AdminHeader title={batch.name}>
                    <Grid item style={{ textAlign: 'right' }}>
                        <TextField
                            type="text"
                            value={formatMoney(allSelectedAmount)}
                            label="Vouchers selected"
                            disabled
                        />
                    </Grid>
                    <Grid item style={{ textAlign: 'right' }}>
                        <TextField
                            type="text"
                            value={formatMoney(allPayoutsAmount)}
                            label="Payment amount"
                            disabled
                        />
                    </Grid>
                    <Grid item style={{ textAlign: 'right' }}>
                        <TextField
                            type="text"
                            value={formatMoney(allPayoutsAmount - allSelectedAmount)}
                            label="Over/under payed"
                            disabled
                        />
                    </Grid>
                    <Grid item style={{ textAlign: 'right' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            style={{ margin: '10px' }}
                            onClick={onSave}
                        >
                            Save
                        </Button>
                    </Grid>
                </AdminHeader>
            </Grid>
            <Grid item xs={12}>
                <Table style={{ backgroundColor: 'white' }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Payout</TableCell>
                            <TableCell>Client</TableCell>
                            <TableCell>V #</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Activity Date</TableCell>
                            <TableCell>Qty.</TableCell>
                            <TableCell>Product name</TableCell>
                            <TableCell>Vendor Conf</TableCell>
                            <TableCell>Amount due</TableCell>
                            <TableCell>Pay</TableCell>
                            <TableCell>Pay amount</TableCell>
                            <TableCell>Over / under</TableCell>
                            <TableCell>Note</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {payouts.map(payout => {
                            const inBatch = payoutsToBatch.find(p => p.payout_id === payout.id);
                            return (
                                <TableRow key={payout.id}>
                                    <TableCell>{payout.id}</TableCell>
                                    <TableCell>{payout.client}</TableCell>
                                    <TableCell>
                                        <Link
                                            to={`/client/bookings/${payout.booking_item_id}`}
                                            target="_blank"
                                        >
                                            {payout.booking_item_id}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{payout.activity}</TableCell>
                                    <TableCell>{formatDateTime(payout.activityDate)}</TableCell>
                                    <TableCell>
                                        {payout.qty}
                                        {typeof payout.qtyAlt !== 'undefined' && (
                                            <>
                                                <br />
                                                <small
                                                    style={{ whiteSpace: 'nowrap' }}
                                                >{` (${payout.qtyAlt})`}</small>
                                            </>
                                        )}
                                    </TableCell>
                                    <TableCell>{payout.productName}</TableCell>
                                    <TableCell>{payout.vendorConf}</TableCell>
                                    <TableCell>{formatMoney(payout.amountDue)}</TableCell>
                                    <TableCell>
                                        <input
                                            type="checkbox"
                                            checked={!!inBatch}
                                            onChange={e => {
                                                if (e.target.checked) {
                                                    setPayoutsToBatch([
                                                        ...payoutsToBatch,
                                                        {
                                                            payout_id: payout.id,
                                                            pay_amount: payout.amountDue,
                                                            note: ''
                                                        }
                                                    ]);
                                                } else {
                                                    setPayoutsToBatch(
                                                        payoutsToBatch.filter(
                                                            p => p.payout_id !== payout.id
                                                        )
                                                    );
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            name="amount"
                                            type="number"
                                            value={inBatch ? inBatch.pay_amount : ''}
                                            disabled={!inBatch}
                                            textAlign={'right'}
                                            onChange={e => {
                                                const newAmount = parseFloat(e.target.value);
                                                setPayoutsToBatch(
                                                    payoutsToBatch.map(p =>
                                                        p.payout_id === payout.id
                                                            ? { ...p, pay_amount: newAmount }
                                                            : p
                                                    )
                                                );
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            name="overUnder"
                                            type="number"
                                            textAlign={'right'}
                                            disabled={!inBatch}
                                            value={
                                                !inBatch
                                                    ? ''
                                                    : payout.amountDue -
                                                      (inBatch ? inBatch.pay_amount : 0)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            name="note"
                                            type="text"
                                            textAlign={'right'}
                                            disabled={!inBatch}
                                            value={inBatch ? inBatch.note : ''}
                                            onChange={e => {
                                                setPayoutsToBatch(
                                                    payoutsToBatch.map(p =>
                                                        p.payout_id === payout.id
                                                            ? { ...p, note: e.target.value }
                                                            : p
                                                    )
                                                );
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Grid>
        </Grid>
    );
}
export default BatchPayoutTable;
